<template>
  <div
    :class="[
      'asc-scc-details-table-item',
      {
        'asc-scc-details-table-item--noItems': !hasItems,
      },
    ]"
  >
    <div class="asc-scc-details-table-item--header">
      <asc-checkbox
        v-model="isChecked"
        :label="item.text.toLowerCase()"
        :disabled="!item.items.length"
        :checked="item.checked"
        :indeterminate="isIndeterminate"
        @change="handleUpdateItemData(null, item, isChecked)"
      />
    </div>
    <div class="asc-scc-details-table-item--details">
      <div class="asc-scc-details-table-item--details-header">
        <span>{{ selectionLabel }}:</span>
        <span class="atachapter-title">{{ selectedItems }} </span>
      </div>
      <template v-if="isExpanded && !hasItemsGrouped">
        <div class="asc-scc-details-table-item--details-items">
          <div
            v-for="(childItem, index) in item.items"
            :key="index"
            class="asc-scc-details-table-item--details-items-item"
          >
            <asc-checkbox
              v-model="childItem.checked"
              class="asc-checkbox"
              :label="childItem.text.toLowerCase()"
              :checked="childItem.checked"
              @change="handleUpdateItemData(item, childItem, childItem.checked)"
            />
          </div>
        </div>
      </template>
      <template v-if="isExpanded && hasItemsGrouped">
        <div class="asc-scc-details-table-item--details-items">
          <div
            v-for="(group, groupIndex) in getGroups"
            :key="groupIndex"
            class="asc-scc-details-table-item--details-items-group"
          >
            <div class="asc-scc-details-table-item--details-items-group-name">
              {{ group.name }}
            </div>
            <div
              v-for="(childItem, index) in getGroupItems(group)"
              :key="index"
              class="asc-scc-details-table-item--details-items-item"
            >
              <asc-checkbox
                v-model="childItem.checked"
                class="asc-checkbox"
                :label="childItem.text.toLowerCase()"
                :checked="childItem.checked"
                @change="
                  handleUpdateItemData(item, childItem, childItem.checked)
                "
              />
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="asc-scc-details-table-item--expand-collapse">
      <asc-accent-button
        button-text=""
        :on-click="handleExpandCollapse"
        :icon="getExpandCollapseIcon"
        :disabled="!hasItems"
        icon-only
        secondary
        size="20"
      ></asc-accent-button>
    </div>
  </div>
</template>

<script>
import AscAccentButton from '@/components/atoms/AscAccentButton'
import AscCheckbox from '@/components/atoms/AscCheckbox'

export default {
  name: 'AscSubscriptionCenterDetailsTableItem',

  components: {
    AscAccentButton,
    AscCheckbox,
  },

  props: {
    item: {
      type: Object,
      default: () => {},
    },
    selectionLabel: {
      type: String,
      default: '',
    },
    groups: {
      type: Object,
      default: () => ({
        hasGroups: false,
      }),
    },
  },

  data() {
    return {
      isExpanded: this.item.expanded,
      isChecked: this.item.checked,
      isIndeterminate: this.item.indeterminate,
      selectedItems: '',
    }
  },

  computed: {
    hasGroups() {
      return this.groups.hasGroups
    },

    hasItems() {
      return this.item.items && this.item.items.length
    },

    getGroups() {
      return this.groups.groups
    },

    hasItemsGrouped() {
      return this.hasItems
        ? Object.prototype.hasOwnProperty.call(this.item.items[0], 'group')
        : false
    },

    getExpandCollapseIcon() {
      return this.isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
    },
  },

  watch: {
    item: {
      deep: true,
      immediate: true,
      handler(newItem) {
        this.isChecked = newItem.checked
        this.isExpanded = newItem.expanded
        this.isIndeterminate = newItem.indeterminate
        this.selectedItems = this.item.items
          .filter(item => item.checked)
          .map(item => item.text)
          .join(', ')
          .toLowerCase()
      },
    },
  },

  methods: {
    getGroupItems(group) {
      return this.item.items.filter(item => item.group === group.value)
    },

    handleExpandCollapse() {
      this.isExpanded = !this.isExpanded
      this.$emit('onExpandCollapse', this.item, this.isExpanded)
    },

    handleUpdateItemData(itemParent, item, checked) {
      this.$emit('onTableItemUpdate', itemParent, item, checked)
      this.$emit('onTableItemUpdate:value', item.value, checked)
    },
  },
}
</script>

<style lang="scss">
.asc-scc-details-table-item {
  display: flex;
  border: 1px solid $gray-300;
  padding: 10px 16px;
  border-width: 0 0 0.5px 0;
  border-style: solid;
  border-color: $gray-300;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  justify-content: space-between;
  min-height: 38px;

  &--header {
    display: flex;
    flex-direction: column;
    flex-grow: 0;
    flex-basis: 300px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .asc-checkbox {
      margin-top: 8px;

      label {
        color: $primary-black;
        font-size: 14px;
        letter-spacing: 0;
        text-transform: capitalize;
      }
    }
  }

  &--details {
    padding-top: 6px;
    max-width: calc(100% - 300px - 40px);
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-basis: calc(100% - 300px - 40px);

    &-header {
      max-width: 100%;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      color: $primary-black;
      font-size: 14px;
      letter-spacing: 0;
      line-height: 18px;

      .atachapter-title {
        text-transform: capitalize;
      }
    }

    &-items {
      margin-top: 12px;

      &-group {
        &-name {
          color: $gray-700;
          font-size: 13px;
          letter-spacing: 0.1px;
          line-height: 17px;
          text-transform: uppercase;
          margin-bottom: 10px;
        }
      }

      &-item {
        margin-bottom: 8px;
        text-transform: capitalize;
      }
    }
  }

  &--expand-collapse {
    width: 40px;
    margin-top: 6px;

    .v-btn {
      border: 0 !important;
      padding: 0 !important;
      max-height: 20px !important;

      &:hover,
      &:focus {
        background-color: $primary-white !important;

        &::before {
          background-color: $primary-white !important;
        }
      }

      .v-icon {
        font-size: 20px !important;
        height: 16px;
        width: 20px;
      }
    }
  }

  &:last-child {
    border-bottom: none;
  }
}
</style>
